import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { ARTIST_BIO_LONG_MAX, ARTIST_BIO_SHORT_MAX } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { bioCounter } from '../bio/bioRules';
import { profileBioMessages as b } from '../bio/profileBioMessages';
import { profileMessages as m } from '../profileMessages';
import { useCopyText } from '../useCopyText';
import { spotifyBioButtonShown, spotifyBioUploadState } from '../spotify/spotifyBioState';
import { SpotifyBioStateLine } from '../spotify/SpotifyBioStateLine';
import { spotifyBioMessages as sb } from '../spotify/spotifyBioMessages';
import { ProfileCard } from './ProfileCard';
import { ProfileChipBadge } from './ProfileChipBadge';
import type { ProfileCardProps } from './profileCardProps';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** One bio text on the card: the head with the counter, then the text or the placeholder. */
const BioText: React.FC<{
  title: string;
  hint: string;
  text: string;
  placeholder: string;
  max: number;
  tall?: boolean;
}> = ({ title, hint, text, placeholder, max, tall = false }) => {
  const intl = useIntl();
  const counter = bioCounter(text, max);
  return (
    <div className={styles.bioField}>
      <div className={styles.bioHead}>
        <b>{title}</b>
        <small>{hint}</small>
        <span className={clsx(styles.count, counter.over > 0 && styles.countOver)}>
          {intl.formatMessage(b.counter, { count: counter.count, max })}
        </span>
      </div>
      <div className={clsx(styles.text, tall && styles.textTall, text === '' && styles.textPlaceholder)}>
        {text === '' ? placeholder : text}
      </div>
    </div>
  );
};

/**
 * The bio card (design a1, a2): the short and the long bio, Edit, "Write it for me", Copy, and
 * "Upload bio to Spotify" with Auto upload access (the long bio goes to Spotify for Artists).
 */
const ProfileBioCardComponent: React.FC<{ card: ProfileCardProps }> = ({ card }) => {
  const { profile, narrow, openTool, openBioSheet } = card;
  const intl = useIntl();
  const { copy, copied } = useCopyText();
  const { bioShort, bioLong } = profile.bio;
  const isEmpty = bioShort === '' && bioLong === '';
  const canUpload = spotifyBioButtonShown(profile);
  const uploaded = spotifyBioUploadState(profile).kind !== 'none';

  return (
    <ProfileCard
      service="bio"
      name={intl.formatMessage(m.bioName)}
      size={intl.formatMessage(narrow ? m.bioSizeNarrow : m.bioSize)}
      chip={
        <ProfileChipBadge
          chip={{ tone: isEmpty ? 'nil' : 'ok', text: isEmpty ? m.chipEmpty : m.chipSaved }}
          testId="profile-chip-bio"
        />
      }
      wide
      testId="profile-card-bio"
    >
      <div className={styles.bio}>
        <BioText
          title={intl.formatMessage(b.shortTitle)}
          hint={intl.formatMessage(b.shortHint)}
          text={bioShort}
          placeholder={intl.formatMessage(b.shortPlaceholder)}
          max={ARTIST_BIO_SHORT_MAX}
        />
        <BioText
          title={intl.formatMessage(b.longTitle)}
          hint={intl.formatMessage(b.longHint)}
          text={bioLong}
          placeholder={intl.formatMessage(b.longPlaceholder)}
          max={ARTIST_BIO_LONG_MAX}
          tall
        />
      </div>
      <div className={styles.row}>
        <SMLButton className={styles.sm} onClick={() => openTool('bio')}>
          {intl.formatMessage(m.edit)}
        </SMLButton>
        <SMLButton className={styles.sm} onClick={() => openTool('bio')}>
          {intl.formatMessage(m.writeForMe)}
        </SMLButton>
        {bioShort !== '' && (
          <SMLButton className={styles.sm} onClick={() => copy('short', bioShort)}>
            {intl.formatMessage(copied === 'short' ? m.copied : m.copyShort)}
          </SMLButton>
        )}
        {bioLong !== '' && (
          <SMLButton className={styles.sm} onClick={() => copy('long', bioLong)}>
            {intl.formatMessage(copied === 'long' ? m.copied : m.copyLong)}
          </SMLButton>
        )}
        {canUpload && (
          <SMLButton className={styles.sm} onClick={openBioSheet}>
            <span className={styles.spotDot} aria-hidden="true" />
            {intl.formatMessage(narrow ? sb.buttonShort : sb.button)}
          </SMLButton>
        )}
        <span className={styles.grow} />
        {!narrow && uploaded && <SpotifyBioStateLine profile={profile} />}
        {!narrow && !uploaded && !canUpload && <span className={styles.meta}>{intl.formatMessage(m.bioMeta)}</span>}
      </div>
    </ProfileCard>
  );
};

export const ProfileBioCard = TypedMemo(ProfileBioCardComponent);
